import { useCallback, useEffect, useState } from "react";
import { http, toastApiError, downloadUrl } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "../lib/notify";
import { Armchair, PackageX, FileText } from "lucide-react";

export default function PersonnelAssets({ personId, personName }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => http.get(`/fixed-assets?assignee_personnel_id=${personId}`).then((r) => setItems(r.data)).catch((e) => toastApiError(e)), [personId]);
  useEffect(() => { if (personId) load(); }, [personId, load]);
  const [released, setReleased] = useState({});

  const release = async (a) => {
    if (!window.confirm(`"${a.name}" demirbaşının ${personName || "bu personel"} üzerindeki zimmeti kaldırılsın mı? Geçmiş demirbaş kartında saklanır.`)) return;
    try {
      const { data } = await http.post(`/fixed-assets/${a.id}/release`);
      toast.success("Zimmet kaldırıldı — iade/teslim tutanağı oluşturulup demirbaş arşivine eklendi");
      setReleased((r) => ({ ...r, [a.id]: data }));
      load();
    } catch (e) { toastApiError(e); }
  };

  if (!personId) return <div className="text-xs text-slate-500 p-3" data-testid="pf-assets-new">Zimmet bilgileri personel kaydedildikten sonra görünür.</div>;
  if (items === null) return <div className="text-xs text-slate-500 p-3">Yükleniyor…</div>;
  return (
    <div className="space-y-3" data-testid="pf-assets">
      <div className="text-sm font-bold text-indigo-600 border-b border-slate-200 pb-1">Zimmet Bilgileri <span className="text-xs font-semibold text-slate-500">— {items.length} demirbaş</span></div>
      {items.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap text-xs" data-testid="pf-assets-toolbar">
          <span className="text-slate-500">Tek tutanak: bu personeldeki tüm demirbaşlar için</span>
          <a href={downloadUrl(`/fixed-assets/tutanak?ids=${items.map((a) => a.id).join(",")}`)} target="_blank" rel="noreferrer" data-testid="pf-assets-bulk-tutanak" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-700 text-white text-[11px] font-semibold hover:bg-rose-800"><FileText className="w-3.5 h-3.5" /> Toplu Tutanak PDF ({items.length})</a>
        </div>
      )}
      {Object.keys(released).length > 0 && (
        <div className="rounded-md border border-sky-300 bg-sky-50 p-3 text-xs" data-testid="pf-assets-released">
          <div className="font-semibold text-sky-900 mb-1">İade edilen demirbaşlar — teslim tutanağı:</div>
          <ul className="space-y-0.5">{Object.values(released).map((a) => <li key={a.id} className="flex items-center justify-between gap-2"><span>{a.name}{a.serial_no ? ` (${a.serial_no})` : ""}</span><a href={downloadUrl(`/fixed-assets/${a.id}/teslim-tutanak`)} target="_blank" rel="noreferrer" data-testid={`pf-asset-teslim-${a.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-700 text-white font-semibold hover:bg-sky-800"><FileText className="w-3 h-3" /> Teslim Tutanağı PDF</a></li>)}</ul>
        </div>
      )}
      {items.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-slate-300 rounded-lg" data-testid="pf-assets-empty">
          <Armchair className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <div className="text-sm font-semibold text-slate-700">Bu personele zimmetli demirbaş yok</div>
          <div className="text-xs text-slate-500 mt-1">Zimmet, Demirbaşlar ekranında demirbaş kartından "Kime Zimmetli" alanı ile tanımlanır.</div>
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full tbl">
            <thead className="bg-slate-50">
              <tr>
                {["Demirbaş Adı", "Seri Numarası", "Cinsi", "Zimmet Tarihi"].map((h) => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold whitespace-nowrap">{h}</th>)}
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Tutanak</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Zimmeti Kaldır</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`pf-asset-row-${a.id}`}>
                  <td className="px-3 py-2 text-xs font-semibold text-slate-900">{a.name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{a.serial_no || "—"}</td>
                  <td className="px-3 py-2 text-xs">{a.kind || "—"}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(a.assigned_at)}</td>
                  <td className="px-3 py-2 text-right"><a href={downloadUrl(`/fixed-assets/${a.id}/tutanak`)} target="_blank" rel="noreferrer" data-testid={`pf-asset-tutanak-${a.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-semibold hover:bg-rose-100"><FileText className="w-3.5 h-3.5" /> PDF</a></td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => release(a)} data-testid={`pf-asset-release-${a.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 text-[11px] font-semibold hover:bg-red-100"><PackageX className="w-3.5 h-3.5" /> Zimmeti Kaldır</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
